"use client";

import { useState, useEffect, useCallback } from "react";
import { CitizensAPI } from "@/lib/api";
import { CitizenRequest, CitizenCategory } from "@/types";
import { formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "sonner";
import { CheckCircle, XCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export default function CitizenRequestsPage() {
  const [requests, setRequests] = useState<CitizenRequest[]>([]);
  const [categories, setCategories] = useState<CitizenCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    "null"
  );
  const [totalRequests, setTotalRequests] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<CitizenRequest | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject">("approve");
  const limit = 10;

  const fetchCategories = useCallback(async () => {
    try {
      const data = await CitizensAPI.getCategories();
      if (Array.isArray(data)) {
        setCategories(data);
      }
    } catch (error) {
      toast.error("Failed to load categories");
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, total } = await CitizensAPI.getPendingRequests(
        page,
        limit,
        selectedCategory !== "null" ? parseInt(selectedCategory) : undefined
      );
      setRequests(data);
      setTotalRequests(total);
    } catch (error) {
      toast.error("Failed to load citizen requests");
      setRequests([]);
      setTotalRequests(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const totalPages = Math.ceil(totalRequests / limit);

  const handleReviewClick = (request: CitizenRequest) => {
    setSelectedRequest(request);
    setIsDialogOpen(true);
  };

  const handleApproveClick = (request: CitizenRequest) => {
    setSelectedRequest(request);
    setActionType("approve");
    setIsDialogOpen(true);
  };

  const handleRejectClick = (request: CitizenRequest) => {
    setSelectedRequest(request);
    setActionType("reject");
    setIsDialogOpen(true);
  };

  const handleStatusUpdate = async (status: "approved" | "rejected") => {
    if (!selectedRequest) return;
    setIsProcessing(true);
    try {
      await CitizensAPI.updateRequestStatus(
        selectedRequest.request_id,
        status,
        status === "rejected" ? rejectReason : undefined
      );

      toast.success(
        `${selectedRequest.citizen_name} has been ${
          status === "approved" ? "approved" : "rejected"
        }`
      );
      fetchRequests();
      setIsDialogOpen(false);
      setRejectReason(""); // Reset reject reason
    } catch (error) {
      toast.error(`Failed to ${status} citizen`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = () => handleStatusUpdate("approved");
  const handleReject = () => handleStatusUpdate("rejected");

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Citizen Requests</h1>
          <p className="text-muted-foreground">
            Review and approve new citizen profile requests
          </p>
        </div>
        <Select
          value={selectedCategory}
          onValueChange={setSelectedCategory}
          defaultValue="null"
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by category">
              {selectedCategory === "null"
                ? "All Categories"
                : categories.find(
                    (c) => c.category_id.toString() === selectedCategory
                  )?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="null">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem
                key={category.category_id}
                value={category.category_id.toString()}
              >
                {category.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Justification</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Decision Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-48 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="h-8 w-24 animate-pulse rounded bg-muted ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No pending requests found
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request.request_id}>
                  <TableCell>{request.citizen_name}</TableCell>
                  <TableCell>
                    {categories.find(
                      (c) => c.category_id === request.category_id
                    )?.label || "N/A"}
                  </TableCell>
                  <TableCell>
                    {request.justification?.substring(0, 50) || "N/A"}
                    {request.justification &&
                      request.justification.length > 50 &&
                      "..."}
                  </TableCell>
                  <TableCell>{formatDate(request.created_date)}</TableCell>
                  <TableCell>
                    {request.status !== "pending"
                      ? formatDate(request.modified_date)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "rounded-full px-2 py-1 text-xs",
                        request.is_duplicate
                          ? "bg-yellow-100 text-yellow-800"
                          : request.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : request.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      )}
                    >
                      {request.is_duplicate ? "Duplicate" : request.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReviewClick(request)}
                      >
                        Review
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRejectClick(request)}
                      >
                        <XCircle className="mr-1 h-4 w-4" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApproveClick(request)}
                      >
                        <CheckCircle className="mr-1 h-4 w-4" />
                        Approve
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) setPage(page - 1);
                }}
                className={page === 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = page;
              if (page < 3) {
                pageNum = i + 1;
              } else if (page > totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              if (pageNum > 0 && pageNum <= totalPages) {
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(pageNum);
                      }}
                      isActive={pageNum === page}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              }
              return null;
            })}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) setPage(page + 1);
                }}
                className={
                  page === totalPages ? "pointer-events-none opacity-50" : ""
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve"
                ? "Approve Citizen Request"
                : "Reject Citizen Request"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve"
                ? "Review and confirm the approval of this citizen profile."
                : "Provide a reason for rejecting this citizen profile request."}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="rounded-md border p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Name
                    </p>
                    <p className="font-medium">
                      {selectedRequest.citizen_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Category
                    </p>
                    <p>{selectedRequest.category?.label}</p>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground">
                    Justification
                  </p>
                  <p className="whitespace-pre-wrap">
                    {selectedRequest.justification}
                  </p>
                </div>

                {selectedRequest.additional_comments && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-muted-foreground">
                      Additional Comments
                    </p>
                    <p className="whitespace-pre-wrap">
                      {selectedRequest.additional_comments}
                    </p>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-4">
                  {selectedRequest.twitter_url && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Twitter
                      </p>
                      <a
                        href={selectedRequest.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        View Profile
                      </a>
                    </div>
                  )}
                  {selectedRequest.instagram_url && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Instagram
                      </p>
                      <a
                        href={selectedRequest.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        View Profile
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {actionType === "reject" && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Rejection Reason</p>
                  <Textarea
                    placeholder="Explain why this citizen profile is being rejected..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant={actionType === "approve" ? "default" : "destructive"}
              onClick={actionType === "approve" ? handleApprove : handleReject}
              disabled={
                isProcessing || (actionType === "reject" && !rejectReason)
              }
            >
              {isProcessing
                ? "Processing..."
                : actionType === "approve"
                ? "Approve Citizen"
                : "Reject Citizen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
